import { WorkItem } from '../components/workitems/workItemTypes';
import type {
  JobDeletionConflict,
  JobDeletionPreview,
  JobDeletionResult,
  LinkedTicketSummary,
  LinkedTicketsResponse,
  MarkCollectedRequest,
  MarkCollectedResult,
  ReleaseDeviceRequest,
  ReleaseDeviceResult,
  ReturnDeviceResult,
  UncollectRequest,
  UncollectResult,
  WorkItemCustomerChange,
} from '@techgitter/contracts';

import { readApiError as readErrorMessage } from './readApiError';

const API_BASE_URL = '/api';

// Shared in-flight promise so concurrent callers (StrictMode double-mount,
// view transitions) reuse one network request instead of racing and
// aborting each other.
let inFlightAllWorkItems: Promise<WorkItem[]> | null = null;

/**
 * Fetch all work items from the API.
 * Handles both `{ items: [...] }` envelope and direct array responses.
 */
export async function getAllWorkItems(signal?: AbortSignal): Promise<WorkItem[]> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  if (inFlightAllWorkItems) {
    // Subscribe to the shared fetch; honour the caller's signal locally
    // without cancelling the underlying request for other subscribers.
    const shared = inFlightAllWorkItems;
    return new Promise<WorkItem[]>((resolve, reject) => {
      const onAbort = () => reject(new DOMException('Aborted', 'AbortError'));
      signal?.addEventListener('abort', onAbort, { once: true });
      shared.then(
        v => { signal?.removeEventListener('abort', onAbort); resolve(v); },
        e => { signal?.removeEventListener('abort', onAbort); reject(e); },
      );
    });
  }

  inFlightAllWorkItems = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/workitems`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch work items: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      const items = Array.isArray(data) ? data : (data.items ?? []);
      return items as WorkItem[];
    } finally {
      inFlightAllWorkItems = null;
    }
  })();

  return getAllWorkItems(signal);
}

/**
 * Reorder work items by updating their displayIndex in Dataverse.
 */
export async function reorderWorkItems(
  items: { guid: string; type: string; displayIndex: number }[],
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/workitems/reorder`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    throw new Error(`Failed to reorder work items: ${response.status} ${response.statusText}`);
  }
}

/**
 * Reassign a work item to a different technician via the orchestrator.
 * Returns immediately (202 Accepted) — the orchestrator broadcasts
 * workitems:changed via SignalR when the reassignment completes.
 *
 * The 202 only means "queued". Reassign reaches RepairShopr through the same
 * PUT /tickets/{id} as a status change and can be refused the same way, which
 * arrives later on `jobs:status`; returning the orchestrator's `messageId`
 * is what lets the caller correlate that verdict — see `jobStatusWatcher`.
 */
export async function reassignWorkItem(
  guid: string,
  ticketId: string,
  newAssigneeFullName: string,
  options?: { displayIndex?: number; type?: string },
): Promise<{ messageId?: string }> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/reassign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ticketId,
      newAssigneeFullName,
      ...(options?.displayIndex != null && options?.type
        ? { displayIndex: options.displayIndex, type: options.type }
        : {}),
    }),
  });
  if (!response.ok) {
    throw new Error(`Failed to reassign work item: ${response.status} ${response.statusText}`);
  }
  const body = await response.json().catch(() => ({}));
  return { messageId: body?.messageId };
}

/**
 * Reassign a job/ticket to a different customer. Unlike reassign/status (which
 * proxy the orchestrator), this is a synchronous RS → DV write — RepairShopr
 * first (`PUT /tickets/{id}`), then the Dataverse job mirror — mirroring the
 * invoice customer-change flow. The back end broadcasts workitems:changed and
 * returns the refreshed work item.
 *
 * Only jobs are supported; invoices already raised from the ticket keep their
 * own customer.
 */
export async function changeWorkItemCustomer(
  guid: string,
  body: WorkItemCustomerChange,
  signal?: AbortSignal,
): Promise<WorkItem> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/customer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to change customer: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * Update a work item's status via the orchestrator.
 *
 * Returns immediately (202 Accepted) — the orchestrator updates RepairShopr
 * and Dataverse, then broadcasts workitems:changed via SignalR.
 *
 * Resolving with the returned `messageId` matters: the 202 only says the
 * message was queued, so a RepairShopr refusal (an incomplete worksheet, say)
 * lands long after this promise settles. The caller correlates that failure
 * off `jobs:status` by this id — without it the rejection is invisible and the
 * status silently reverts.
 */
export async function updateWorkItemStatus(
  guid: string,
  ticketId: string,
  status: string,
  type: string,
): Promise<{ messageId?: string }> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticketId, status, type }),
  });
  if (!response.ok) {
    throw new Error(`Failed to update status: ${response.status} ${response.statusText}`);
  }
  const body = await response.json().catch(() => ({}));
  return { messageId: body?.messageId };
}

/**
 * Delete an appointment. Jobs go through `deleteJob` instead, which shows the
 * user what the delete takes with it first — the server refuses a job delete
 * without the preview's fingerprint.
 */
export async function deleteWorkItem(
  guid: string,
  ticketId: string,
  type: string = 'job',
): Promise<{ status: string; guid?: string; messageId?: string }> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticketId: ticketId ? Number(ticketId) || undefined : undefined, type }),
  });
  if (!response.ok) {
    throw new Error(`Failed to delete work item: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null);
  return (body && typeof body.error === 'string' && body.error) || `${fallback}: ${response.status}`;
}

/**
 * What deleting a job would remove, leave, and be blocked by. Only categories
 * with something in them are returned. Admin or Manager only.
 */
export async function getJobDeletionPreview(guid: string): Promise<JobDeletionPreview> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/delete-preview`);
  if (!response.ok) {
    throw new Error(await errorMessage(response, 'Failed to load what this delete would remove'));
  }
  return response.json();
}

export type DeleteJobOutcome =
  | { ok: true; result: JobDeletionResult }
  /** Nothing changed: blocked by a paid invoice, or the list changed since it was shown. */
  | { ok: false; conflict: JobDeletionConflict };

/**
 * Delete a job through the cascade (docs/architecture/job-deletion.md in
 * sm4rt-api). `fingerprint` is the preview's — the server refuses with a fresh
 * preview if what it would delete no longer matches what the user confirmed.
 * Any other failure throws, with the server's message.
 */
export async function deleteJob(
  guid: string,
  args: { ticketId?: string; fingerprint: string; reason?: string },
): Promise<DeleteJobOutcome> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      type: 'job',
      ticketId: args.ticketId ? Number(args.ticketId) || undefined : undefined,
      fingerprint: args.fingerprint,
      reason: args.reason?.trim() || undefined,
    }),
  });
  if (response.status === 409) {
    const body = await response.json().catch(() => null);
    if (body?.preview) return { ok: false, conflict: body as JobDeletionConflict };
  }
  if (!response.ok) {
    throw new Error(await errorMessage(response, 'Failed to delete the job'));
  }
  return { ok: true, result: await response.json() };
}

/**
 * Search work items via the API search parameter.
 */
export async function searchWorkItems(
  searchTerm: string,
  signal?: AbortSignal
): Promise<WorkItem[]> {
  const params = new URLSearchParams({ search: searchTerm });
  const response = await fetch(`${API_BASE_URL}/workitems?${params}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to search work items: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  const items = Array.isArray(data) ? data : (data.items ?? []);
  return items as WorkItem[];
}

/**
 * Search resolved/historic work items with date range and text filters.
 */
export interface HistorySearchParams {
  search?: string;
  searchFields?: string;   // comma-separated: "name,subject,comments"
  technician?: string;     // exact technician fullname, empty = all
  resolvedFrom?: string;   // YYYY-MM-DD
  resolvedTo?: string;     // YYYY-MM-DD
  includeDeleted?: boolean; // admin-only: include soft-deleted items
  page?: number;
  pageSize?: number;
}

export interface PaginatedWorkItems {
  items: WorkItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export async function searchWorkItemHistory(
  params: HistorySearchParams,
  signal?: AbortSignal,
): Promise<PaginatedWorkItems> {
  const query = new URLSearchParams();
  if (params.resolvedFrom) query.set('resolvedFrom', params.resolvedFrom);
  if (params.resolvedTo) query.set('resolvedTo', params.resolvedTo);
  if (params.search) query.set('search', params.search);
  if (params.searchFields) query.set('searchFields', params.searchFields);
  if (params.technician) query.set('technician', params.technician);
  if (params.includeDeleted) query.set('includeDeleted', 'true');
  if (params.page != null) query.set('page', String(params.page));
  if (params.pageSize != null) query.set('pageSize', String(params.pageSize));

  const response = await fetch(`${API_BASE_URL}/workitems/history?${query}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to search work item history: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return {
    items: data.items ?? [],
    pagination: data.pagination ?? {
      page: 1, pageSize: 50, totalItems: 0, totalPages: 0, hasNext: false, hasPrevious: false,
    },
  };
}

/**
 * Look up a single work item by its RS ticket number.
 */
export async function getWorkItemByNumber(
  number: number,
  signal?: AbortSignal,
): Promise<WorkItem | null> {
  const response = await fetch(`${API_BASE_URL}/workitems/by-number/${number}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (response.status === 404) return null;

  if (!response.ok) {
    throw new Error(`Failed to fetch work item: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<WorkItem>;
}

/**
 * Look up a single work item by id, regardless of status.
 * The list endpoint defaults to active-only, so resolved jobs (e.g. the
 * original ticket referenced by an in_store_return) need this dedicated
 * lookup.
 */
export async function getWorkItemById(
  id: string,
  signal?: AbortSignal,
): Promise<WorkItem | null> {
  const response = await fetch(`${API_BASE_URL}/workitems/${id}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (response.status === 404) return null;

  if (!response.ok) {
    throw new Error(`Failed to fetch work item: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<WorkItem>;
}

/**
 * Mark a work item collected — the customer took the finished device home
 * (terminal handover; distinct from the temporary "device release" flow).
 * `collectedAt` / `collectedBy` are stamped server-side from the caller's
 * token, and a `cr001_work_item_audits` row is appended.
 *
 * An outstanding balance is not refused: show the warning first, then send
 * `balanceWarningShown: true` so the audit row records that it was shown
 * (docs/qr-scan-workflow-requirements.md §9, D16). Idempotent — a collected
 * job answers `alreadyCollected: true`.
 */
export async function markWorkItemCollected(
  guid: string,
  body: MarkCollectedRequest,
  signal?: AbortSignal,
): Promise<MarkCollectedResult> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/mark-collected`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to mark collected'));
  }
  return response.json() as Promise<MarkCollectedResult>;
}

/** Undo a collection (a mis-scan). Appends a `device_uncollected` audit row;
 *  the collection row stays. */
export async function uncollectWorkItem(
  guid: string,
  body: UncollectRequest,
  signal?: AbortSignal,
): Promise<UncollectResult> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/uncollect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to undo collection'));
  }
  return response.json() as Promise<UncollectResult>;
}

/**
 * Release the device to the customer while the job waits for parts or a
 * supplier quote (docs/device-release-awaiting-parts.md). The job keeps its
 * status; `deviceWithCustomer` is a marker beside it. 409 outside the
 * eligible statuses (see `utils/deviceRelease.ts`).
 */
export async function releaseWorkItemDevice(
  guid: string,
  body: ReleaseDeviceRequest,
  signal?: AbortSignal,
): Promise<ReleaseDeviceResult> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/release-device`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to release device'));
  }
  return response.json() as Promise<ReleaseDeviceResult>;
}

/** The released device is back in the shop — the manual fallback; a Drop Off
 *  check-in against the job clears it on its own. */
export async function returnWorkItemDevice(
  guid: string,
  signal?: AbortSignal,
): Promise<ReturnDeviceResult> {
  const response = await fetch(`${API_BASE_URL}/workitems/${guid}/return-device`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
    signal,
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to mark device returned'));
  }
  return response.json() as Promise<ReturnDeviceResult>;
}

export async function linkAppointmentToJob(appointmentGuid: string, jobGuid: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/workitems/${jobGuid}/link-appointment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ appointmentGuid }),
  });
  if (!response.ok) {
    throw new Error(`Failed to link appointment: ${response.status} ${response.statusText}`);
  }
}

export async function unlinkAppointmentFromJob(appointmentGuid: string, jobGuid: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/workitems/${jobGuid}/link-appointment/${appointmentGuid}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to unlink appointment: ${response.status} ${response.statusText}`);
  }
}

/**
 * Jobs raised from this one via the ticket form's Link button (the reverse of
 * `WorkItem.linkedFromJobGuid`). Fetched for one job at a time — the edit form
 * and an expanded dashboard card — never per list row.
 */
export async function fetchLinkedTickets(
  jobGuid: string,
  signal?: AbortSignal,
): Promise<LinkedTicketSummary[]> {
  const response = await fetch(
    `${API_BASE_URL}/workitems/${encodeURIComponent(jobGuid)}/linked-tickets`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(`Failed to load linked tickets: ${response.status} ${response.statusText}`);
  }
  const body = (await response.json()) as LinkedTicketsResponse;
  return body.items ?? [];
}
